/**
 * HTTP client.
 *
 * Owns three concerns the rest of the app should never repeat: attaching the
 * access token, unwrapping the `{ success, data }` envelope, and refreshing a
 * token exactly once when a request comes back 401.
 */

import { reportLicenseProblem } from '@/store/license'
import type { ApiResponse, PaginatedResponse, ProblemDetail } from '@/types/api'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** A failed request, carrying the server's problem document. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly problem: ProblemDetail | null
  readonly fieldErrors: Record<string, string>

  constructor(message: string, status: number, code: string, problem: ProblemDetail | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.problem = problem
    this.fieldErrors = Object.fromEntries(
      (problem?.errors ?? [])
        .filter((e) => e.field)
        .map((e) => [e.field as string, e.message]),
    )
  }

  /** True when retrying verbatim could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429
  }
}

type TokenGetter = () => string | null
type RefreshHandler = () => Promise<string | null>
type UnauthorizedHandler = () => void

let getAccessToken: TokenGetter = () => null
let refreshTokens: RefreshHandler = async () => null
let onUnauthorized: UnauthorizedHandler = () => {}

export function configureClient(options: {
  getAccessToken: TokenGetter
  refreshTokens: RefreshHandler
  onUnauthorized: UnauthorizedHandler
}): void {
  getAccessToken = options.getAccessToken
  refreshTokens = options.refreshTokens
  onUnauthorized = options.onUnauthorized
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  query?: Record<string, unknown>
  /** Set false for the auth endpoints themselves, to avoid a refresh loop. */
  auth?: boolean
  raw?: boolean
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const url = `${BASE_URL}${path}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    // Repeat the key for arrays — FastAPI reads `?statuses=a&statuses=b`.
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') params.append(key, String(item))
      })
    } else {
      params.append(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function toApiError(response: Response): Promise<ApiError> {
  let problem: ProblemDetail | null = null
  try {
    problem = (await response.json()) as ProblemDetail
  } catch {
    // A non-JSON error body (a proxy 502, say) still deserves a clean message.
  }
  const message =
    problem?.detail || problem?.title || `Request failed with status ${response.status}`
  // 402: the user may not apply (no plan, quota used, feature not in plan).
  // Every caller gets the upgrade dialog from here; none has to wire it.
  if (response.status === 402) reportLicenseProblem(problem, response.status)
  return new ApiError(message, response.status, problem?.code ?? 'unknown_error', problem)
}

async function send(path: string, options: RequestOptions, isRetry = false): Promise<Response> {
  const { body, query, auth = true, headers, ...rest } = options

  const requestHeaders = new Headers(headers)
  const isFormData = body instanceof FormData
  if (body !== undefined && !isFormData) {
    requestHeaders.set('Content-Type', 'application/json')
  }
  if (auth) {
    const token = getAccessToken()
    if (token) requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    headers: requestHeaders,
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  })

  // One refresh attempt, then give up — a second 401 means the session is gone.
  if (response.status === 401 && auth && !isRetry) {
    const refreshed = await refreshTokens()
    if (refreshed) return send(path, options, true)
    onUnauthorized()
  }

  return response
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options)
  if (!response.ok) throw await toApiError(response)
  if (response.status === 204) return undefined as T

  const payload = await response.json()
  // Endpoints return either an envelope or a bare object (health probes).
  if (options.raw) return payload as T
  return (payload as ApiResponse<T>).data ?? (payload as T)
}

/** Fetch a file with the session's token and hand it to the browser to save. */
async function download(path: string, fileName: string): Promise<void> {
  const response = await send(path, { method: 'GET' })
  if (!response.ok) throw await toApiError(response)
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/** Fetch a protected file (a screenshot, say) as a Blob, with the session's token. */
async function blob(path: string): Promise<Blob> {
  const response = await send(path, { method: 'GET' })
  if (!response.ok) throw await toApiError(response)
  return response.blob()
}

export const api = {
  blob,
  download,
  get: <T>(path: string, query?: Record<string, unknown>) =>
    request<T>(path, { method: 'GET', query }),

  /** Keeps `pageInfo` alongside the rows, for paginated endpoints. */
  getPage: <T>(path: string, query?: Record<string, unknown>) =>
    request<PaginatedResponse<T>>(path, { method: 'GET', query, raw: true }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData, query?: Record<string, unknown>) =>
    request<T>(path, { method: 'POST', body: formData, query }),

  /** For non-enveloped endpoints such as /health. */
  raw: <T>(path: string, query?: Record<string, unknown>) =>
    request<T>(path, { method: 'GET', query, raw: true, auth: false }),

  /** Unauthenticated POST, used by login/register/refresh. */
  postPublic: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body, auth: false }),
}
